// src/models/beverageModels.js
/**
 * Enhanced Beverage Data Models for AI Integration
 * Supports comprehensive beverage intelligence with AI enhancement
 *
 * @typedef {Object} BeverageSchema - Base beverage schema
 * @typedef {Object} AIEnhancementSchema - AI enhancement data structure
 * @typedef {Object} MenuItemSchema - Menu item integration schema
 * @typedef {Object} GuestPreferenceSchema - Guest preference profile
 * @typedef {Object} ComparisonSchema - Beverage comparison results
 *
 * Terminology:
 * - mocktail: non-alcoholic cocktail
 * - malolactic: secondary fermentation process in winemaking
 * - BJCP: Beer Judge Certification Program
 * - collectibility: investment and collection potential
 * - umami: fifth basic taste (savory)
 */

// Base beverage structure
export const baseBeverageSchema = {
  // Original restaurant data
  id: "",
  restaurantId: "",
  name: "",
  category: "", // wine, beer, spirit, cocktail, mocktail, coffee, tea, sake, cider, soft_drink
  price: 0,
  description: "",
  imageUrl: "",
  inStock: true,
  order: 0,
  createdAt: null,
  updatedAt: null,

  // Enhanced fields for AI integration
  aiEnhanced: false,
  aiEnhancedAt: null,
  aiVersion: "1.0",
};

// AI Enhancement structure
export const aiEnhancementSchema = {
  // Professional ratings from multiple sources
  ratings: [
    {
      source: "", // "Wine Spectator", "Robert Parker", "Untappd", etc.
      score: 0,
      scale: 100, // Rating scale (100, 20, 5, etc.)
      review: "",
      reviewDate: null,
      reviewerName: "",
    },
  ],

  // Comprehensive tasting notes
  tastingNotes: {
    appearance: {
      color: "",
      clarity: "",
      intensity: "",
    },
    aroma: {
      primary: [], // ["citrus", "floral", "mineral"]
      secondary: [], // ["oak", "vanilla", "spice"]
      tertiary: [], // ["honey", "nuts", "leather"]
      intensity: "", // "light", "moderate", "pronounced"
      complexity: "", // "simple", "moderate", "complex"
    },
    palate: {
      sweetness: "", // "bone dry", "off-dry", "medium-sweet", "sweet"
      acidity: "", // "low", "medium", "high"
      tannins: "", // wines: "low", "medium", "high"
      alcohol: "", // "low", "medium", "high"
      body: "", // "light", "medium", "full"
      flavors: [], // ["dark fruit", "spice", "earth"]
      texture: "", // "smooth", "velvety", "grippy"
      balance: "", // "poor", "good", "excellent"
    },
    finish: {
      length: "", // "short", "medium", "long"
      intensity: "", // "light", "moderate", "pronounced"
      characteristics: [], // ["fruity", "spicy", "mineral"]
    },
  },

  // Producer and origin information
  producer: {
    name: "",
    website: "",
    founded: null,
    location: {
      country: "",
      region: "",
      subregion: "",
      appellation: "", // For wines
      coordinates: {
        lat: 0,
        lng: 0,
      },
    },
    history: "",
    philosophy: "",
    techniques: [], // ["organic", "biodynamic", "traditional"]
    accolades: [], // Awards and recognitions
  },

  // Category-specific details
  categoryDetails: {
    // Wine-specific
    wine: {
      vintage: null,
      varietal: [], // ["Cabernet Sauvignon", "Merlot"]
      blend: "", // Percentage breakdown if blend
      vineyard: "",
      ageability: {
        drinkNow: true,
        peakYears: "", // "2025-2030"
        cellarPotential: "", // years
      },
      winemaking: {
        fermentation: "",
        aging: "", // "18 months in French oak"
        malolacticFermentation: false,
        filtration: "",
        clarification: "",
      },
      terroir: {
        soil: "",
        climate: "",
        elevation: "",
        exposure: "",
      },
    },

    // Beer-specific
    beer: {
      style: "", // "IPA", "Stout", "Lager"
      bjcpStyle: "", // BJCP style code
      abv: 0,
      ibu: 0, // International Bitterness Units
      srm: 0, // Standard Reference Method (color)
      og: 0, // Original Gravity
      fg: 0, // Final Gravity
      hops: [], // Hop varieties used
      malts: [], // Malt types
      yeast: "", // Yeast strain
      brewing: {
        mashTemp: "",
        boilTime: "",
        fermentationTemp: "",
        conditioning: "",
      },
      seasonality: "", // "Year-round", "Seasonal", "Limited"
    },

    // Spirit-specific
    spirit: {
      type: "", // "Whiskey", "Vodka", "Gin", etc.
      subtype: "", // "Bourbon", "Scotch", "Rye"
      age: "", // "12 years", "NAS"
      proof: 0,
      abv: 0,
      distillation: {
        method: "", // "Pot still", "Column still"
        numberOfDistillations: 0,
        mashbill: "", // For whiskeys
        botanicals: [], // For gin
      },
      maturation: {
        caskType: [], // ["ex-bourbon", "sherry", "new oak"]
        maturationPeriod: "",
        warehouse: "",
        climate: "",
      },
      collectibility: {
        limited: false,
        bottleNumber: "",
        totalBottles: 0,
        investment: "", // "Excellent", "Good", "Moderate"
      },
    },

    // Cocktail-specific
    cocktail: {
      baseSpirit: "", // Primary spirit
      style: "", // "Shaken", "Stirred", "Built"
      glassware: "", // "Coupe", "Rocks", "Highball"
      garnish: "",
      difficulty: "", // "Easy", "Moderate", "Advanced"
      ingredients: [
        {
          name: "",
          amount: "",
          unit: "", // "oz", "ml", "dash"
          type: "", // "spirit", "liqueur", "mixer"
        },
      ],
      technique: "", // Preparation method
      origin: "", // Historical background
      variations: [], // Common variations
    },
  },

  // Food pairing recommendations
  pairings: {
    recommended: [], // General pairing categories
    menuItems: [], // Specific restaurant menu items
    pairingPrinciples: "", // Why these pairings work
    contrasting: [], // Contrasting flavors that work
    complementary: [], // Complementary flavors
    occasions: [], // "Date night", "Business dinner"
    seasons: [], // "Spring", "Summer", "Fall", "Winter"
  },

  // Market and investment data
  market: {
    averagePrice: 0,
    priceRange: {
      low: 0,
      high: 0,
    },
    availability: "", // "Common", "Limited", "Rare"
    investmentPotential: "", // "High", "Medium", "Low"
    priceHistory: [
      {
        date: null,
        price: 0,
        source: "",
      },
    ],
  },

  // AI metadata
  aiMetadata: {
    confidence: 0, // 0-1 scale
    sources: [], // Data sources used
    lastUpdated: null,
    version: "",
    processingNotes: "",
  },
};

// Menu item integration schema
export const menuItemSchema = {
  id: "",
  restaurantId: "",
  name: "",
  description: "",
  category: "", // "appetizer", "entree", "dessert"
  price: 0,
  ingredients: [],
  allergens: [],
  dietary: [], // "vegetarian", "vegan", "gluten-free"

  // AI-enhanced pairing data
  pairingProfile: {
    flavors: [], // Primary flavors
    intensity: "", // "light", "medium", "bold"
    preparation: "", // "grilled", "roasted", "raw"
    sauce: "", // Sauce type if applicable
    spiceLevel: "", // Heat level
    richness: "", // Fat content impact
    acidity: "", // Dish acidity level
    umami: "", // Umami level
  },

  // Recommended beverage pairings
  beveragePairings: [
    {
      beverageId: "",
      beverageName: "",
      pairingScore: 0, // 0-1 confidence
      reason: "", // Why this pairing works
      type: "", // "classic", "creative", "contrasting"
    },
  ],
};

// Guest preference profile schema
export const guestPreferenceSchema = {
  id: "",
  restaurantId: "",
  guestId: "", // Anonymous or user ID

  // Preference categories
  preferences: {
    wine: {
      styles: [], // "bold reds", "crisp whites"
      regions: [], // "Napa Valley", "Burgundy"
      varieties: [], // "Cabernet", "Chardonnay"
      priceRange: { min: 0, max: 0 },
      sweetness: "", // "dry", "off-dry", "sweet"
      body: "", // "light", "medium", "full"
      tannins: "", // "low", "medium", "high"
    },
    beer: {
      styles: [], // "IPA", "Stout", "Lager"
      abvRange: { min: 0, max: 0 },
      ibuRange: { min: 0, max: 0 },
      seasonal: [], // "Summer", "Winter"
      local: true, // Preference for local breweries
    },
    spirits: {
      types: [], // "Whiskey", "Gin", "Vodka"
      aged: true, // Preference for aged spirits
      proof: "", // "standard", "cask strength"
      regions: [], // "Scotch", "Bourbon", "Japanese"
      cocktails: [], // Preferred cocktail styles
    },
  },

  // Tasting history
  history: [
    {
      beverageId: "",
      rating: 0, // 1-5 stars
      notes: "",
      date: null,
      occasion: "",
    },
  ],

  // Favorites and dislikes
  favorites: [],
  dislikes: [],

  // Dietary restrictions and preferences
  dietary: {
    vegetarian: false,
    vegan: false,
    glutenFree: false,
    allergies: [],
    spiceTolerance: "", // "mild", "medium", "hot"
  },

  // Visit context
  visitContext: {
    occasion: "", // "date", "business", "celebration"
    groupSize: 0,
    budget: "", // "casual", "moderate", "upscale"
    timeOfDay: "", // "lunch", "dinner", "late night"
  },
};

// Comparison result schema
export const comparisonSchema = {
  id: "",
  restaurantId: "",

  // Items being compared
  referenceItem: {
    name: "",
    category: "",
    source: "", // "external" or beverage ID if from menu
    description: "",
  },

  // Similar items from restaurant inventory
  matches: [
    {
      beverageId: "",
      beverageName: "",
      similarityScore: 0, // 0-1 scale
      description: "",
      category: "",
      price: 0,
    },
  ],

  // Comparison metadata
  comparedAt: null,
  aiModel: "",
  confidence: 0,
};

// Export all schemas
const beverageModels = {
  baseBeverageSchema,
  aiEnhancementSchema,
  menuItemSchema,
  guestPreferenceSchema,
  comparisonSchema,
};

export default beverageModels;
